import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Card, Input, message, Modal, Popconfirm, Space, Table, Tabs, Tag, Tooltip} from 'antd';
import {
    ApiOutlined,
    DeleteOutlined,
    DiffOutlined,
    DownloadOutlined,
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import {createApi, deleteApi, deleteApiBatch, exportApisDoc, getApiPreview, getApis, updateApi} from '../api/apiApi';
import ManagementHeader from './ManagementHeader';
import {pickList} from '../api/http';
import useApiPermission from '../hooks/useApiPermission';
import PermissionButton from './common/PermissionButton';
import PermissionGate from './common/PermissionGate';
import APIDetailDrawer from './APIDetailDrawer';
import {useSearchParams} from 'react-router-dom';
import CompareApiModal from './CompareApiModal';
import BulkProgressModal from './common/BulkProgressModal';

const APIManagement = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [bulkRegistering, setBulkRegistering] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [exporting, setExporting] = useState(false);
  // 진행 모달 상태
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressTotal, setProgressTotal] = useState(0);
  const [progressCurrent, setProgressCurrent] = useState(0);
  const [progressSuccess, setProgressSuccess] = useState(0);
  const [progressFail, setProgressFail] = useState(0);
  const [progressFailures, setProgressFailures] = useState([]);
  const [progressEntries, setProgressEntries] = useState([]); // [{key,label,status,reason, apiId, methodType, apiUrl}]
  const abortRef = useRef(false);

  // 목록 필터 상태
  const [methodFilter, setMethodFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [urlFilter, setUrlFilter] = useState('');

  // 상세 드로어 상태
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [detailEditMode, setDetailEditMode] = useState(false);

  // 비교 모달 상태
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareMethod, setCompareMethod] = useState('');
  const [comparePath, setComparePath] = useState('');
  const [compareApiId, setCompareApiId] = useState(undefined);

  // URL 쿼리 상태 동기화
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMode = (() => {
    const changed = searchParams.get('changed');
    const registered = searchParams.get('registered');
    if (changed === 'true') return 'changed';
    if (registered === 'false') return 'unregistered';
    if (registered === 'true') return 'registered';
    return 'all';
  })();
  const [filterMode, setFilterMode] = useState(initialMode);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const before = params.toString();
    params.delete('changed');
    params.delete('registered');
    if (filterMode === 'changed') params.set('changed', 'true');
    else if (filterMode === 'unregistered') params.set('registered', 'false');
    else if (filterMode === 'registered') params.set('registered', 'true');
    const after = params.toString();
    if (before !== after) {
      setSearchParams(params);
    }
  }, [filterMode, searchParams, setSearchParams]);

  // 권한 훅
  const { canCall, loading: permLoading } = useApiPermission();
  const canRead = canCall(getApis);
  const canCreate = canCall(createApi);
  const canDeleteSingle = canCall(deleteApi);
  const canDeleteMulti = canCall(deleteApiBatch);
  const canUpdate = canCall(updateApi);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (permLoading) return;
      if (!canRead) { setData([]); message.warning('API 목록 조회 권한이 없습니다.'); return; }
      let params = {};
      if (filterMode === 'changed') params = { changed: true };
      else if (filterMode === 'unregistered') params = { registered: false };
      else if (filterMode === 'registered') params = { registered: true };
      const res = await getApis(params);
      const list = pickList(res);
      setData(list);
    } catch (e) { message.error('API 목록 조회 실패'); }
    finally { setLoading(false); }
  }, [permLoading, canRead, filterMode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    if (permLoading || !canCreate) { message.warning('생성 권한이 없습니다.'); return; }
    setDetailRecord({});
    setDetailEditMode(true);
    setDetailOpen(true);
  };
  const openEdit = (record) => { setDetailRecord(record); setDetailEditMode(true); setDetailOpen(true); };
  const openRegisterFromRow = (record) => {
    if (permLoading || !canCreate) { message.warning('생성 권한이 없습니다.'); return; }
    setDetailRecord(record);
    setDetailEditMode(false);
    setDetailOpen(true);
  };
  const openDetail = (record) => { setDetailRecord(record); setDetailEditMode(false); setDetailOpen(true); };
  const openCompare = (record) => {
    const method = record?.methodType || '';
    const path = record?.apiUrl || '';
    const apiId = record?.apiId;
    if (!method || !path) {
      message.warning('비교할 Method/URL이 없습니다.');
      return;
    }
    // 모달 오픈으로 전환
    setCompareMethod(method);
    setComparePath(path);
    setCompareApiId(apiId);
    setCompareOpen(true);
  };

  const handleDelete = async (record) => {
    if (permLoading || !canDeleteSingle) { message.warning('삭제 권한이 없습니다.'); return; }
    try { await deleteApi(record.apiId); message.success('삭제되었습니다'); fetchData(); }
    catch { message.error('삭제 실패'); }
  };

  const handleBatchDelete = () => {
    if (permLoading || !canDeleteMulti) { message.warning('일괄 삭제 권한이 없습니다.'); return; }
    const registeredIds = data.filter(r => selectedKeys.includes(getRowKey(r)) && r.apiId).map(r => r.apiId);
    if (!registeredIds.length) { message.warning('삭제할 등록된 API를 선택하세요.'); return; }
    Modal.confirm({
      title: '선택 삭제',
      content: `${registeredIds.length}개 등록된 API를 삭제하시겠습니까?`,
      okText: '삭제', okType: 'danger', cancelText: '취소',
      onOk: async () => { try { await deleteApiBatch(registeredIds); message.success('삭제 완료'); setSelectedKeys([]); fetchData(); } catch { message.error('일괄 삭제 실패'); } }
    });
  };

  // 공통 처리 유틸: 대상 목록 처리(프리뷰 조회 후 갱신) - 제한된 병렬 처리 풀 적용
  const processTargets = useCallback(async (targets) => {
    setProgressOpen(true);
    setBulkUpdating(true);
    setProgressTotal(targets.length);
    setProgressCurrent(0);
    setProgressSuccess(0);
    setProgressFail(0);
    setProgressFailures([]);
    setProgressEntries([]);
    abortRef.current = false;

    const CONCURRENCY = Math.min(5, Math.max(1, targets.length));
    let succ = 0; let fail = 0; let cur = 0;
    let nextIndex = 0;

    const runOne = async (t) => {
      let entry;
      try {
        let pv = null;
        try {
          pv = await getApiPreview(t.methodType, t.apiUrl);
        } catch (e) {
          entry = { key: getRowKey(t), label: `${t.methodType} ${t.apiUrl}`, status: 'fail', reason: e?.message || '프리뷰 조회 실패', apiId: t.apiId, methodType: t.methodType, apiUrl: t.apiUrl };
          setProgressFailures(prev => [...prev, { key: entry.key, label: entry.label, reason: entry.reason, apiId: t.apiId, methodType: t.methodType, apiUrl: t.apiUrl }]);
          setProgressEntries(prev => [...prev, entry]);
          return { ok: false };
        }
        await updateApi(t.apiId, {
          methodType: t.methodType,
          apiNm: (pv?.apiNm && String(pv.apiNm).trim()) ? pv.apiNm : (t.apiNm || `${t.methodType} ${t.apiUrl}`),
          apiUrl: t.apiUrl,
          apiDsctn: (pv && Object.prototype.hasOwnProperty.call(pv, 'apiDsctn')) ? pv.apiDsctn : t.apiDsctn,
          header: pv.header || '',
          body: pv.body || '',
          response: pv.response || ''
        });
        entry = { key: getRowKey(t), label: `${t.methodType} ${t.apiUrl}`, status: 'success', apiId: t.apiId, methodType: t.methodType, apiUrl: t.apiUrl };
        setProgressEntries(prev => [...prev, entry]);
        return { ok: true };
      } catch (e) {
        entry = { key: getRowKey(t), label: `${t.methodType} ${t.apiUrl}`, status: 'fail', reason: e?.message || '갱신 실패', apiId: t.apiId, methodType: t.methodType, apiUrl: t.apiUrl };
        setProgressFailures(prev => [...prev, { key: entry.key, label: entry.label, reason: entry.reason, apiId: t.apiId, methodType: t.methodType, apiUrl: t.apiUrl }]);
        setProgressEntries(prev => [...prev, entry]);
        return { ok: false };
      }
    };

    const worker = async () => {
      while (!abortRef.current) {
        const i = nextIndex;
        if (i >= targets.length) break;
        nextIndex = i + 1;
        const t = targets[i];
        const res = await runOne(t);
        cur += 1; setProgressCurrent(cur);
        if (res.ok) { succ += 1; setProgressSuccess(succ); }
        else { fail += 1; setProgressFail(fail); }
      }
    };

    const workers = Array.from({ length: CONCURRENCY }, () => worker());
    await Promise.all(workers);

    setBulkUpdating(false);
    message.info(`갱신 완료: ${succ}건 / 실패: ${fail}건${abortRef.current ? ' (중지됨)' : ''}`);
    setSelectedKeys([]);
    fetchData();
  }, [fetchData]);

  const handleBatchUpdate = () => {
    if (permLoading || !canUpdate) { message.warning('갱신 권한이 없습니다.'); return; }
    const targets = data.filter(r => selectedKeys.includes(getRowKey(r)) && r.apiId && (r.registerType || '').trim() === '변경');
    if (!targets.length) { message.warning("'변경' 항목을 선택하세요."); return; }
    Modal.confirm({
      title: '선택 갱신',
      content: `변경된 API ${targets.length}개를 프리뷰 내용으로 갱신하시겠습니까?`,
      okText: '갱신', cancelText: '취소',
      onOk: async () => { await processTargets(targets); }
    });
  };

  const handleRetryAll = async () => {
    if (bulkUpdating) return;
    const retryTargets = (progressEntries || []).filter(e => e.status === 'fail').map(f => ({
      apiId: f.apiId,
      methodType: f.methodType,
      apiUrl: f.apiUrl,
      apiNm: undefined,
      apiDsctn: undefined,
    }));
    if (!retryTargets.length) { message.info('재시도할 항목이 없습니다.'); return; }
    await processTargets(retryTargets);
  };

  const handleRetryOne = async (row) => {
    if (bulkUpdating) return;
    const t = { apiId: row.apiId, methodType: row.methodType, apiUrl: row.apiUrl };
    if (!t.apiId || !t.methodType || !t.apiUrl) { message.warning('재시도에 필요한 정보가 부족합니다.'); return; }
    await processTargets([t]);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      // filterMode -> 서버 파라미터 매핑
      const params = (() => {
        switch (filterMode) {
          case 'changed': return { changed: true };
          case 'unregistered': return { registered: false };
          case 'registered': return { registered: true };
          case 'deleted': return { type: 'deleted' };
          default: return {};
        }
      })();
      const res = await exportApisDoc(params);
      const blob = res?.data instanceof Blob ? res.data : new Blob([res?.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      // 파일명 파싱
      const cd = (res?.headers?.['content-disposition'] || res?.headers?.['Content-Disposition'] || '');
      let filename = 'api-spec.docx';
      const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd);
      if (m) {
        const raw = (m[1] || m[2] || '').trim();
        try { filename = decodeURIComponent(raw); } catch { filename = raw; }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error('명세서 다운로드에 실패했습니다');
    } finally {
      setExporting(false);
    }
  };

  const statusRank = (r) => {
    const t = (r.registerType || '').trim();
    if (t === '삭제') return 0;
    if (t === '변경') return 1;
    if (t === '미등록') return 2;
    // apiId 존재 시 등록 취급
    if (r.apiId) return 3;
    return 99;
  };

  const resolveRegisterLabel = (r) => {
    const t = (r.registerType || '').trim();
    if (t === '삭제') return '삭제';
    if (t === '변경') return '변경';
    if (t === '미등록') return '미등록';
    if (r.apiId) return '등록';
    return '미등록';
  };

  const resolveRegisterColor = (label) => {
    switch (label) {
      case '삭제': return 'volcano';
      case '변경': return 'orange';
      case '등록': return 'green';
      case '미등록': return 'red';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: '등록구분', key: 'registerType', dataIndex: 'registerType', width: 100, align:'center',
      sorter: (a, b) => statusRank(a) - statusRank(b),
      render: (_, r) => {
        const label = resolveRegisterLabel(r);
        const color = resolveRegisterColor(label);
        return <Tag color={color}>{label}</Tag>;
      }
    },
    { title: 'Method', dataIndex: 'methodType', key: 'methodType', width: 100, sorter: (a,b)=> (a.methodType||'').localeCompare(b.methodType||''), render: v => <Tag color={'blue'}>{v}</Tag> },
    { title: 'API명', dataIndex: 'apiNm', key: 'apiNm', width: 220, sorter: (a,b)=> (a.apiNm||'').localeCompare(b.apiNm||'') },
    { title: 'URL', dataIndex: 'apiUrl', key: 'apiUrl', ellipsis: true, sorter: (a,b)=> (a.apiUrl||'').localeCompare(b.apiUrl||'') },
    { title: '설명', dataIndex: 'apiDsctn', key: 'apiDsctn', ellipsis: true, render: v => v || '-' },
    { title: '작업', key: 'actions', width: 340, render: (_, r) => (
      <Space size={4} wrap>
        <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r)}>상세</Button>
        <Button size="small" icon={<DiffOutlined />} onClick={() => openCompare(r)}>비교</Button>
        {r.apiId && (
          <>
            <PermissionButton size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} require={updateApi}>수정</PermissionButton>
            <Popconfirm title="삭제" okText="삭제" cancelText="취소" onConfirm={() => handleDelete(r)}>
              <PermissionButton size="small" danger icon={<DeleteOutlined />} require={deleteApi}>삭제</PermissionButton>
            </Popconfirm>
          </>
        )}
        {!r.apiId && (
          <Tooltip title="미등록 API입니다. 클릭하여 등록하세요.">
            <PermissionButton size="small" type="primary" onClick={() => openRegisterFromRow(r)} require={createApi}>등록</PermissionButton>
          </Tooltip>
        )}
      </Space>
    ) }
  ];

  const getRowKey = (row) => row.apiId || (row.methodType + '::' + row.apiUrl);

  const handleBatchRegister = () => {
    if (permLoading || !canCreate) { message.warning('생성 권한이 없습니다.'); return; }
    const targets = data.filter((r) => selectedKeys.includes(getRowKey(r)) && !r.apiId);
    if (!targets.length) { message.warning('등록할 미등록 API를 선택하세요.'); return; }
    Modal.confirm({
      title: '선택 등록',
      content: `미등록 API ${targets.length}개를 등록하시겠습니까?`,
      okText: '등록', cancelText: '취소',
      onOk: async () => {
        setBulkRegistering(true);
        let success = 0; let fail = 0;
        for (const t of targets) {
          try {
            let pv;
            try { pv = await getApiPreview(t.methodType, t.apiUrl); } catch (e) { pv = null; }
            await createApi({
              methodType: t.methodType,
              apiNm: t.apiNm || `${t.methodType} ${t.apiUrl}`,
              apiDsctn: t.apiDsctn,
              apiUrl: t.apiUrl,
              header: pv?.header,
              body: pv?.body,
              response: pv?.response,
            });
            success++;
          } catch (e) { fail++; }
        }
        setBulkRegistering(false);
        message.info(`등록 완료: ${success}건 / 실패: ${fail}건`);
        setSelectedKeys([]);
        fetchData();
      }
    });
  };

  const filteredData = useMemo(() => {
    // 1) 구분 필터(변경/미등록/등록/삭제) 우선 적용
    const modeFiltered = (() => {
      const t = (filterMode || 'all');
      const labelOf = (r) => resolveRegisterLabel(r);
      if (t === 'changed') return data.filter(r => labelOf(r) === '변경');
      if (t === 'unregistered') return data.filter(r => labelOf(r) === '미등록');
      if (t === 'registered') return data.filter(r => labelOf(r) === '등록');
      if (t === 'deleted') return data.filter(r => labelOf(r) === '삭제');
      return data;
    })();

    // 2) 텍스트 필터(Method/API명/URL)
    const mf = methodFilter.trim().toLowerCase();
    const nf = nameFilter.trim().toLowerCase();
    const uf = urlFilter.trim().toLowerCase();
    if (!mf && !nf && !uf) return modeFiltered;
    return modeFiltered.filter(a => (
      (!mf || (a.methodType || '').toLowerCase().includes(mf)) &&
      (!nf || (a.apiNm || '').toLowerCase().includes(nf)) &&
      (!uf || (a.apiUrl || '').toLowerCase().includes(uf))
    ));
  }, [data, filterMode, methodFilter, nameFilter, urlFilter]);

  const changedSelectedExists = useMemo(() => data.some(r => selectedKeys.includes(getRowKey(r)) && (r.registerType || '').trim() === '변경' && r.apiId), [data, selectedKeys]);

  const headerActions = (
    <>
      <PermissionButton type="primary" icon={<PlusOutlined />} onClick={openCreate} require={createApi}>API 생성</PermissionButton>
      <Button type="primary" ghost onClick={handleBatchRegister} disabled={bulkRegistering || !data.some(r => selectedKeys.includes(getRowKey(r)) && !r.apiId) || permLoading || !canCreate}>선택등록</Button>
      <Button onClick={handleBatchUpdate} disabled={bulkUpdating || !changedSelectedExists || permLoading || !canUpdate} loading={bulkUpdating}>갱신</Button>
      <Button danger icon={<DeleteOutlined />} disabled={!data.some(r => selectedKeys.includes(getRowKey(r)) && r.apiId) || permLoading || !canDeleteMulti} onClick={handleBatchDelete}>선택삭제</Button>
      <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading || bulkRegistering || bulkUpdating} disabled={permLoading || !canRead}>새로고침</Button>
      <PermissionButton icon={<DownloadOutlined />} type="default" onClick={handleExport} loading={exporting} require={exportApisDoc} style={{ marginLeft: 8 }}>명세서 다운로드</PermissionButton>
      <Input placeholder="Method" allowClear style={{ width:100, marginLeft: 8 }} value={methodFilter} onChange={e=>setMethodFilter(e.target.value)} />
      <Input placeholder="API명" allowClear style={{ width:160 }} value={nameFilter} onChange={e=>setNameFilter(e.target.value)} />
      <Input placeholder="URL" allowClear style={{ width:200 }} value={urlFilter} onChange={e=>setUrlFilter(e.target.value)} />
      <Button onClick={()=>{ setMethodFilter(''); setNameFilter(''); setUrlFilter(''); }} disabled={!methodFilter && !nameFilter && !urlFilter}>필터초기화</Button>
    </>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <ManagementHeader icon={<ApiOutlined />} title="API 관리" actions={headerActions} description="API 리소스를 생성/수정/삭제하고 메서드/URL을 관리합니다." />
      <PermissionGate require={getApis}>
        {({allowed}) => (
          <Card size="small" styles={{ body: { padding: 0 } }} style={{ opacity: allowed ? 1 : 0.5, pointerEvents: allowed ? 'auto' : 'none' }}>
            <Tabs
              activeKey={filterMode}
              onChange={(key) => setFilterMode(key)}
              style={{ padding: '0 16px' }}
              items={[
                {
                  key: 'all',
                  label: '전체',
                  children: (
                    <Table
                      size="small"
                      rowKey={(row) => getRowKey(row)}
                      columns={columns}
                      dataSource={filteredData}
                      loading={loading || bulkRegistering || bulkUpdating}
                      rowSelection={{ selectedRowKeys:selectedKeys, onChange:setSelectedKeys }}
                      pagination={{ pageSize: 15, showSizeChanger:false }}
                      scroll={{ x: 'max-content' }}
                      onRow={(record) => ({ onClick: () => openDetail(record) })}
                    />
                  )
                },
                {
                  key: 'changed',
                  label: '변경',
                  children: (
                    <Table
                      size="small"
                      rowKey={(row) => getRowKey(row)}
                      columns={columns}
                      dataSource={filteredData}
                      loading={loading || bulkRegistering || bulkUpdating}
                      rowSelection={{ selectedRowKeys:selectedKeys, onChange:setSelectedKeys }}
                      pagination={{ pageSize: 15, showSizeChanger:false }}
                      scroll={{ x: 'max-content' }}
                      onRow={(record) => ({ onClick: () => openDetail(record) })}
                    />
                  )
                },
                {
                  key: 'unregistered',
                  label: '미등록',
                  children: (
                    <Table
                      size="small"
                      rowKey={(row) => getRowKey(row)}
                      columns={columns}
                      dataSource={filteredData}
                      loading={loading || bulkRegistering || bulkUpdating}
                      rowSelection={{ selectedRowKeys:selectedKeys, onChange:setSelectedKeys }}
                      pagination={{ pageSize: 15, showSizeChanger:false }}
                      scroll={{ x: 'max-content' }}
                      onRow={(record) => ({ onClick: () => openDetail(record) })}
                    />
                  )
                },
                {
                  key: 'registered',
                  label: '등록',
                  children: (
                    <Table
                      size="small"
                      rowKey={(row) => getRowKey(row)}
                      columns={columns}
                      dataSource={filteredData}
                      loading={loading || bulkRegistering || bulkUpdating}
                      rowSelection={{ selectedRowKeys:selectedKeys, onChange:setSelectedKeys }}
                      pagination={{ pageSize: 15, showSizeChanger:false }}
                      scroll={{ x: 'max-content' }}
                      onRow={(record) => ({ onClick: () => openDetail(record) })}
                    />
                  )
                },
                {
                  key: 'deleted',
                  label: '삭제',
                  children: (
                    <Table
                      size="small"
                      rowKey={(row) => getRowKey(row)}
                      columns={columns}
                      dataSource={filteredData}
                      loading={loading || bulkRegistering || bulkUpdating}
                      rowSelection={{ selectedRowKeys:selectedKeys, onChange:setSelectedKeys }}
                      pagination={{ pageSize: 15, showSizeChanger:false }}
                      scroll={{ x: 'max-content' }}
                      onRow={(record) => ({ onClick: () => openDetail(record) })}
                    />
                  )
                }
              ]}
            />
          </Card>
        )}
      </PermissionGate>
      <APIDetailDrawer
        open={detailOpen}
        record={detailRecord}
        initialEditMode={detailEditMode}
        onClose={() => { setDetailOpen(false); setDetailRecord(null); setDetailEditMode(false); }}
        onRegistered={() => fetchData()}
      />
      <CompareApiModal
        open={compareOpen}
        onClose={() => { setCompareOpen(false); setCompareMethod(''); setComparePath(''); setCompareApiId(undefined); }}
        method={compareMethod}
        path={comparePath}
        apiId={compareApiId}
        onApplied={() => fetchData()}
      />
      <BulkProgressModal
        open={progressOpen}
        title="일괄 갱신"
        total={progressTotal}
        current={progressCurrent}
        success={progressSuccess}
        fail={progressFail}
        entries={progressEntries}
        failures={progressFailures}
        running={bulkUpdating}
        onCancel={() => { if (bulkUpdating) return; setProgressOpen(false); setProgressFailures([]); setProgressEntries([]); }}
        onStop={() => { abortRef.current = true; }}
        onRetryAll={handleRetryAll}
        onRetryOne={handleRetryOne}
      />
    </div>
  );
};

export default APIManagement;
